import { z } from 'zod';

import { HONEYPOT_FIELD, US_STATES } from './intake';

/*
 * The careers application — ROADMAP.md §8.7.
 *
 * **`reference/prototype-copy/careers-form.md` is the specification**, captured
 * field by field off the live form on 2026-08-04. §8.7's own prose summary was
 * short by four things, which is the same silent discard the slice exists to
 * prevent one size smaller: Days Available, Types of Cleaning Experience,
 * reliable transportation, and the "no references" flag.
 *
 * **"The form is the one thing that must not regress."** It is the only surface
 * in this project receiving anything real today — genuine people are applying
 * for genuine jobs through it — so every field the live form collects is
 * collected here, and the integration test reads all of them back.
 */

// ---------------------------------------------------------------------------
// The option lists, in the form's own order and wording
// ---------------------------------------------------------------------------

export const EMPLOYMENT_TYPE_OPTIONS = [
  { value: 'full_time', label: 'Full-Time' },
  { value: 'part_time', label: 'Part-Time' },
  { value: 'either', label: 'Either' },
] as const;

export const EXPERIENCE_YEARS_OPTIONS = [
  { value: 'lt_1', label: 'Less than 1 year' },
  { value: '1_2', label: '1–2 years' },
  { value: '3_5', label: '3–5 years' },
  { value: '6_10', label: '6–10 years' },
  { value: '10_plus', label: '10+ years' },
] as const;

/**
 * "Days Available" — seven options.
 *
 * On the live form these are bare `<button>` elements with no `name`, no `role`
 * and no `aria-pressed`: they submit nothing a form parser can see and a screen
 * reader cannot report their state. Real checkboxes in a `<fieldset>` here.
 */
export const DAY_OPTIONS = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' },
] as const;

/** "Types of Cleaning Experience" — six options, same treatment. */
export const EXPERIENCE_TYPE_OPTIONS = [
  { value: 'residential', label: 'Residential / House' },
  { value: 'commercial', label: 'Commercial / Office' },
  { value: 'deep', label: 'Deep Cleaning' },
  { value: 'move_in_out', label: 'Move-In / Move-Out' },
  { value: 'post_construction', label: 'Post-Construction' },
  { value: 'vacation_rental', label: 'Vacation Rental / Airbnb' },
] as const;

const values = <T extends readonly { value: string }[]>(options: T) =>
  options.map((option) => option.value) as unknown as [string, ...string[]];

export const EMPLOYMENT_TYPE_VALUES = values(EMPLOYMENT_TYPE_OPTIONS);
export const EXPERIENCE_YEARS_VALUES = values(EXPERIENCE_YEARS_OPTIONS);
export const DAY_VALUES = values(DAY_OPTIONS);
export const EXPERIENCE_TYPE_VALUES = values(EXPERIENCE_TYPE_OPTIONS);

/** Never render a raw enum — CLAUDE.md §4. */
function labelFrom(options: readonly { value: string; label: string }[], value: string): string {
  return options.find((option) => option.value === value)?.label ?? value;
}

export const employmentTypeLabel = (value: string) => labelFrom(EMPLOYMENT_TYPE_OPTIONS, value);
export const experienceYearsLabel = (value: string) => labelFrom(EXPERIENCE_YEARS_OPTIONS, value);
export const dayLabel = (value: string) => labelFrom(DAY_OPTIONS, value);
export const experienceTypeLabel = (value: string) => labelFrom(EXPERIENCE_TYPE_OPTIONS, value);

// ---------------------------------------------------------------------------
// The application
// ---------------------------------------------------------------------------

const employerSchema = z.object({
  employerName: z.string().trim().min(1, 'Who did you work for?').max(255),
  jobTitle: z.string().trim().min(1, 'What was your job title?').max(255),
  /** Free text on the form — "Jan 2022 – Mar 2024", "two summers". Never parsed. */
  datesWorked: z.string().trim().max(128).optional(),
  responsibilities: z.string().trim().max(5000).optional(),
  reasonForLeaving: z.string().trim().max(512).optional(),
});

const referenceSchema = z.object({
  name: z.string().trim().min(1, 'Who is the reference?').max(255),
  phone: z.string().trim().min(7, 'That phone number looks too short.').max(32),
  relationship: z.string().trim().max(128).optional(),
});

export const careersApplicationSchema = z
  .object({
    // --- Applicant Information ---------------------------------------------
    fullName: z.string().trim().min(1, 'Please tell us your name.').max(255),
    phone: z.string().trim().min(7, 'That phone number looks too short.').max(32),
    email: z
      .string()
      .trim()
      .min(1, 'We need an email address to reply to.')
      .max(255)
      .pipe(z.email('That does not look like an email address.')),

    // Optional on the live form, so optional here.
    address: z.string().trim().max(255).optional(),
    city: z.string().trim().max(128).optional(),
    postalCode: z.string().trim().max(16).optional(),
    // Blank with no default on the live form — the applicant may be out of state.
    state: z.enum(US_STATES).optional(),

    // --- Position Information ----------------------------------------------
    positionId: z.uuid('Choose the job you are applying for.'),
    desiredPay: z.string().trim().max(64).optional(),
    availableStartDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-09-01.')
      .optional(),
    employmentType: z.enum(EMPLOYMENT_TYPE_VALUES),

    /** *Added 2026-08-04.* Decides whether somebody can cover a Saturday. */
    daysAvailable: z.array(z.enum(DAY_VALUES)).max(7).default([]),

    /*
     * **Stored as the positive while the form asks the negative.**
     *
     * The live form's checkbox is "I don't have Cleaning Experience". A field
     * holding the inverse of its own name is how a read eventually gets it
     * backwards, so the inversion happens once — in the form component — and
     * everything from here on says what it means.
     */
    hasCleaningExperience: z.boolean(),
    experienceYears: z.enum(EXPERIENCE_YEARS_VALUES).optional(),

    /** *Added 2026-08-04.* What makes somebody a Commercial Cleaner. */
    experienceTypes: z.array(z.enum(EXPERIENCE_TYPE_VALUES)).max(6).default([]),

    hasOwnSupplies: z.boolean().default(false),
    /** *Added 2026-08-04.* In Iron County this decides whether somebody is sendable. */
    hasReliableTransportation: z.boolean().default(false),

    // --- Work Experience ---------------------------------------------------
    hasWorkExperience: z.boolean(),
    employers: z.array(employerSchema).max(2).default([]),

    // --- References --------------------------------------------------------
    /** *Added 2026-08-04.* Distinct from having no reference rows. */
    hasReferences: z.boolean(),
    references: z.array(referenceSchema).max(2).default([]),

    // --- Additional Information -------------------------------------------
    additionalInfo: z.string().trim().max(5000).optional(),

    [HONEYPOT_FIELD]: z.string().max(512).optional(),
    renderedAt: z.number().int().positive().optional(),
  })
  /*
   * **Three conditional rules, and each one fixes a contradiction on the live
   * form rather than relaxing it.**
   *
   * The live form marks "Years of Cleaning Experience" required *and* offers a
   * checkbox saying you have none — so an honest applicant with no experience
   * cannot submit. Same for the employer block, which is marked required beside
   * "I don't have Work Experience", and for References. Requiring the detail
   * only when the applicant says they have it is what the checkboxes obviously
   * mean, and it is the difference between a form that turns away a career
   * changer and one that does not.
   */
  .refine((data) => !data.hasCleaningExperience || data.experienceYears !== undefined, {
    message: 'How long have you been cleaning?',
    path: ['experienceYears'],
  })
  .refine((data) => !data.hasWorkExperience || data.employers.length >= 1, {
    message: 'Tell us about your most recent employer.',
    path: ['employers'],
  })
  .refine((data) => !data.hasReferences || data.references.length >= 1, {
    message: 'Add at least one reference.',
    path: ['references'],
  });

export type CareersApplicationInput = z.infer<typeof careersApplicationSchema>;

/** What `/api/public/positions` returns — only what the business advertises. */
export interface HiringPosition {
  id: string;
  name: string;
}
